import Icon from 'components/icons/Icon';
import {
  MdHome,
  MdLock,
  MdSettings,
  MdPeople,
  MdPersonAdd,
  MdGroup,
  MdLink,
  MdAnalytics,
  MdBugReport,
  MdFitnessCenter,
  MdCalendarToday,
} from 'react-icons/md';

// Admin Imports
import MainDashboard from 'views/admin/default';
import AdminDashboard from 'views/admin/dashboard/AdminDashboard';
import { TrainersList } from 'views/admin/trainers';
import Exercises from 'views/common/exercises';
import AgencyApprovals from 'views/admin/approvals/AgencyApprovals';

// Trainer Imports
import {
  TrainerDashboard,
  TrainerClientsList
} from 'views/trainer';

// Agency Imports
import { AgencyDashboard } from 'views/agency';

// Common Imports
import { NewWorkoutDetails } from 'views/common/workouts';
import CalendarPage from 'views/common/calendar';

// Auth Imports
import SignInCentered from 'views/auth/signIn';
import AgencySignup from 'views/auth/agencySignup';
import TrainerSignup from 'views/auth/trainerSignup';
import TrainerGuide from 'views/auth/trainerGuide';

// Public Imports
import PublicGettingStarted from 'views/public/gettingStarted';

// Not Found Page
import NotFound from 'views/common/NotFound';

// Role permissions
import { ROLE_PERMISSIONS, UserRole } from 'utils/rolePermissions';

const routes: RoutesType[] = [
  // Admin Dashboard
  {
    name: 'Dashboard',
    layout: '/admin',
    path: '/default',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <AdminDashboard />,
    showInSidebar: true,
    allowedRoles: ['admin'] as UserRole[],
  },
  {
    name: 'Agency Approvals',
    layout: '/admin',
    path: '/approvals',
    icon: <Icon as={MdPersonAdd} width="20px" height="20px" color="inherit" />,
    component: <AgencyApprovals />,
    showInSidebar: true,
    allowedRoles: ['admin'] as UserRole[],
  },


  // Agency Dashboard 
  {
    name: 'Dashboard',
    layout: '/agency',
    path: '/default',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <AgencyDashboard />,
    showInSidebar: true,
    allowedRoles: ['agency'] as UserRole[], // Only agency can see this
  },

  // Trainer Dashboard 
  {
    name: 'Dashboard',
    layout: '/trainer',
    path: '/default',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <TrainerDashboard />,
    showInSidebar: true,
    allowedRoles: ['trainer'] as UserRole[], // Only trainer can see this
  },  // Trainers Management (Agency only)
  {
    name: 'Trainer Management',
    layout: '/agency',
    path: '/trainers',
    icon: <Icon as={MdPeople} width="20px" height="20px" color="inherit" />,
    component: <TrainersList />,
    showInSidebar: true,
    allowedRoles: ['agency'] as UserRole[], // Only agency can manage trainers
  },

  // Client Management (Trainer only)
  {
    name: 'My Clients',
    layout: '/trainer',
    path: '/my-clients',
    icon: <Icon as={MdGroup} width="20px" height="20px" color="inherit" />,
    component: <TrainerClientsList />,
    showInSidebar: true,
    allowedRoles: ['trainer'] as UserRole[], // Only trainers can see their clients
  },
  // Trainer Client Workout Calendar
  {
    name: 'Workout Calendar',
    layout: '/trainer',
    path: '/calendar',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false, // Removed from sidebar - access through clients page
    allowedRoles: ['trainer'] as UserRole[], // Trainers can see
  },
  {
    name: 'Client Calendar',
    layout: '/trainer',
    path: '/calendar/:clientId',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false,
    allowedRoles: ['trainer'] as UserRole[],
  },

  // Agency Calendar Routes
  {
    name: 'Workout Calendar',
    layout: '/agency',
    path: '/calendar',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false, // Removed from sidebar - access through clients page
    allowedRoles: ['agency'] as UserRole[], // Agency can see
  },
  {
    name: 'Client Calendar',
    layout: '/agency',
    path: '/calendar/:clientId',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false,
    allowedRoles: ['agency'] as UserRole[], // All roles can access
  },

  // Admin Calendar Routes
  {
    name: 'Workout Calendar',
    layout: '/admin',
    path: '/calendar',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false,
    allowedRoles: ['admin'] as UserRole[],
  },
  {
    name: 'Client Calendar',
    layout: '/admin',
    path: '/calendar/:clientId',
    icon: <Icon as={MdCalendarToday} width="20px" height="20px" color="inherit" />,
    component: <CalendarPage />,
    showInSidebar: false,
    allowedRoles: ['agency'] as UserRole[],
  },



  // Exercise Management (Agency can manage all, trainers can manage their own)
  {
    name: 'Exercise Management',
    layout: '/agency',
    path: '/exercises',
    icon: <Icon as={MdFitnessCenter} width="20px" height="20px" color="inherit" />,
    component: <Exercises />,
    showInSidebar: true,
    allowedRoles: ['agency'] as UserRole[], // Agency can manage all exercises
  },
  {
    name: 'Exercise Management',
    layout: '/trainer',
    path: '/exercises',
    icon: <Icon as={MdFitnessCenter} width="20px" height="20px" color="inherit" />,
    component: <Exercises />,
    showInSidebar: true,
    allowedRoles: ['trainer'] as UserRole[], // Trainers can manage their own exercises
  },

  // Hidden routes (not in sidebar)
  {
    name: 'Workout Details',
    layout: '/admin',
    path: '/workouts/:workoutId',
    icon: <Icon as={MdFitnessCenter} width="20px" height="20px" color="inherit" />,
    component: <NewWorkoutDetails />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer'] as UserRole[], // All authenticated users can view workout details
  },
  {
    name: 'Workout Details',
    layout: '/agency',
    path: '/workouts/:workoutId',
    icon: <Icon as={MdFitnessCenter} width="20px" height="20px" color="inherit" />,
    component: <NewWorkoutDetails />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer'] as UserRole[], // All authenticated users can view workout details
  },
  {
    name: 'Trainer Workout Details',
    layout: '/trainer',
    path: '/workouts/:workoutId',
    icon: <Icon as={MdFitnessCenter} width="20px" height="20px" color="inherit" />,
    component: <NewWorkoutDetails />,
    showInSidebar: false,
    allowedRoles: ['trainer'] as UserRole[], // Trainers can view their workout details
  },
  {
    name: 'Sign In',
    layout: '/auth',
    path: '/sign-in',
    icon: <Icon as={MdLock} width="20px" height="20px" color="inherit" />,
    component: <SignInCentered />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access sign in
  },
  {
    name: 'Agency Signup',
    layout: '/auth',
    path: '/agency-signup',
    icon: <Icon as={MdPersonAdd} width="20px" height="20px" color="inherit" />,
    component: <AgencySignup />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access signup
  },
  {
    name: 'Trainer Signup',
    layout: '/auth',
    path: '/trainer-signup',
    icon: <Icon as={MdPersonAdd} width="20px" height="20px" color="inherit" />,
    component: <TrainerSignup />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access signup
  },
  {
    name: 'Trainer Guide',
    layout: '/auth',
    path: '/trainer-guide',
    icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
    component: <TrainerGuide />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access guide
  },

  // Public Getting Started Page
  {
    name: 'Getting Started',
    layout: '/',
    path: '/getting-started',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <PublicGettingStarted />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access
  },

  // 404 Not Found
  {
    name: 'Not Found',
    layout: '/',
    path: '404',
    icon: <Icon as={MdBugReport} width="20px" height="20px" color="inherit" />,
    component: <NotFound />,
    showInSidebar: false,
    allowedRoles: ['admin', 'agency', 'trainer', 'user'] as UserRole[], // Everyone can access 404 page
  }
];

// Function to get routes filtered by user role and layout
export const getRoutesByRole = (userRole: string | null) => {
  if (!userRole) return routes.filter(route => route.layout === '/auth');

  // Get layout based on role
  let userLayout = '/auth';
  if (userRole === 'admin') userLayout = '/admin';
  if (userRole === 'agency') userLayout = '/agency';
  if (userRole === 'trainer') userLayout = '/trainer';

  // Return routes for the user's layout and with proper role permissions
  return routes.filter(route => {
    // If layout matches user's layout
    if (route.layout === userLayout) {
      // If no role restrictions, allow access
      if (!route.allowedRoles) return true;
      // Otherwise check role permissions
      return route.allowedRoles.indexOf(userRole as UserRole) !== -1;
    }
    return false;
  });
};

// Function to get the default dashboard route based on user role
export const getDefaultDashboard = (userRole: string | null) => {
  switch (userRole) {
    case 'admin':
      return '/admin/default';
    case 'agency':
      return '/agency/default';
    case 'trainer':
      return '/trainer/default';
    default:
      return '/'; // Landing page for non-authenticated users
  }
};

export default routes;
